import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams, Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSEO } from "@/lib/seo";
import { api } from "@/lib/api";

import OverviewTab       from "@/components/admin/OverviewTab";
import DealsTab          from "@/components/admin/DealsTab";
import FundsSettlementTab from "@/components/admin/FundsSettlementTab";
import UsersTab          from "@/components/admin/UsersTab";
import ListingsTab       from "@/components/admin/ListingsTab";
import FeaturedTab       from "@/components/admin/FeaturedTab";
import AuditTab          from "@/components/admin/AuditTab";
import StripeTab         from "@/components/admin/StripeTab";
import WiresTab          from "@/components/admin/WiresTab";
import AiTab             from "@/components/admin/AiTab";
import DevToolsTab       from "@/components/admin/DevToolsTab";

import AdminApplications from "@/components/app/AdminApplications";
import AdminDealers      from "@/components/app/AdminDealers";
import AdminDocuments    from "@/components/app/AdminDocuments";
import AdminPortfolioRequests from "@/pages/AdminPortfolioRequests";

const BASE_TABS = [
  { key: "overview",   label: "Overview" },
  { key: "deals",      label: "Deals" },
  { key: "funds",      label: "Funds & Settlement" },
  { key: "users",      label: "Users" },
  { key: "listings",   label: "Listings" },
  { key: "featured",   label: "Featured Listings" },
  { key: "documents",  label: "Documents" },
  { key: "portfolios", label: "Portfolio Requests" },
  { key: "dealers",    label: "Dealers" },
  { key: "applications", label: "Applications" },
  { key: "stripe",     label: "Stripe" },
  { key: "wires",      label: "Wire Receipts" },
  { key: "ai",         label: "AI Assistant" },
  { key: "audit",      label: "Audit Logs" },
];
const DEV_TAB = { key: "devtools", label: "Development Tools" };

export default function AdminDashboard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [devEnabled, setDevEnabled] = useState(false);

  useSEO({ title: "Admin Console | FinanceLocal", noIndex: true });

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/dev/status")
      .then((r) => setDevEnabled(!!r.data?.enabled))
      .catch(() => setDevEnabled(false));
  }, [user]);

  const TABS = useMemo(
    () => (devEnabled ? [...BASE_TABS, DEV_TAB] : BASE_TABS),
    [devEnabled]
  );

  const active = useMemo(() => {
    const t = params.get("tab") || "overview";
    return TABS.find((x) => x.key === t) ? t : "overview";
  }, [params, TABS]);

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    next.set("tab", key);
    setParams(next, { replace: true });
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div data-testid="page-admin-dashboard" className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-border pb-6 mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-overline text-accent">Admin</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Marketplace Control</h1>
          <div className="text-sm text-muted-foreground mt-1">
            {user.email} · <span className="font-mono">{user.role}</span>
          </div>
        </div>
        <div className="flex gap-3 text-xs">
          <Link to="/admin/impersonation-logs" className="font-overline text-muted-foreground hover:text-primary" data-testid="admin-nav-impersonation">
            Impersonation Logs →
          </Link>
        </div>
      </div>

      <Tabs value={active} onValueChange={setTab}>
        <TabsList className="rounded-none bg-transparent border-b border-border w-full justify-start h-auto p-0 overflow-x-auto flex-nowrap">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`admin-tab-${t.key}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 whitespace-nowrap text-[13px]"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview"     className="pt-6"><OverviewTab onJump={setTab} /></TabsContent>
        <TabsContent value="deals"        className="pt-6"><DealsTab /></TabsContent>
        <TabsContent value="funds"        className="pt-6"><FundsSettlementTab /></TabsContent>
        <TabsContent value="users"        className="pt-6"><UsersTab /></TabsContent>
        <TabsContent value="listings"     className="pt-6"><ListingsTab /></TabsContent>
        <TabsContent value="featured"     className="pt-6"><FeaturedTab /></TabsContent>
        <TabsContent value="documents"    className="pt-6"><AdminDocuments /></TabsContent>
        <TabsContent value="portfolios"   className="pt-6"><AdminPortfolioRequests /></TabsContent>
        <TabsContent value="dealers"      className="pt-6"><AdminDealers /></TabsContent>
        <TabsContent value="applications" className="pt-6"><AdminApplications /></TabsContent>
        <TabsContent value="stripe"       className="pt-6"><StripeTab /></TabsContent>
        <TabsContent value="wires"        className="pt-6"><WiresTab /></TabsContent>
        <TabsContent value="ai"           className="pt-6"><AiTab /></TabsContent>
        <TabsContent value="audit"        className="pt-6"><AuditTab /></TabsContent>
        {devEnabled && <TabsContent value="devtools" className="pt-6"><DevToolsTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
